import * as React from 'react';
import { Translation } from 'react-i18next';
import { useMutation } from 'react-query';

import { iFormContactFields } from '@/components/Form/FormContact';
import { NOTIFICATION_DEFAULT_AUTOCLOSE_TIME_IN_MS, PAYMENTS_BACKEND_URL } from '@/config';
import axios from '@/lib/axios';
import { MutationConfig, queryClient } from '@/lib/react-query';
import { useNotificationStore } from '@/stores/notifications';

export type CreateNotificationContact = {
  data: iFormContactFields;
};

export const createNotificationContact = ({
  data,
}: CreateNotificationContact): Promise<CreateNotificationContact> =>
  axios.post(PAYMENTS_BACKEND_URL + 'notification/contact', data);

type UseNotificationContactOptions = {
  config?: MutationConfig<typeof createNotificationContact>;
};

export const useCreateNotificationContact = ({ config }: UseNotificationContactOptions = {}) => {
  const { addNotification } = useNotificationStore();

  return useMutation({
    onMutate: async (creatingNotification: any) => {
      await queryClient.cancelQueries(['notification', creatingNotification]);

      const previousDetails = queryClient.getQueryData<CreateNotificationContact>([
        'notification',
        creatingNotification,
      ]);

      queryClient.setQueryData(['notification', creatingNotification], {
        ...previousDetails,
        ...creatingNotification.data,
      });

      return { previousDetails: previousDetails };
    },
    onError: (_, __, context: any) => {
      if (context?.previousDetails) {
        queryClient.setQueryData(
          ['notification', context.previousDetails],
          context.previousDetails
        );
      }
    },
    onSuccess: (data) => {
      queryClient.refetchQueries(['notification', data]);
      addNotification({
        type: 'success',
        title: (
          <Translation>{(t) => t('Forms.ContactGuessUser.notificationContact.title')}</Translation>
        ),
        message: (
          <Translation>
            {(t) => t('Forms.ContactGuessUser.notificationContact.description')}
          </Translation>
        ),
        autoClose: NOTIFICATION_DEFAULT_AUTOCLOSE_TIME_IN_MS,
      });
    },
    ...config,
    mutationFn: createNotificationContact,
  });
};
